import "./GridPage.css";
import React, { useState } from "react";
import GridButtons from "../GridButtons/GridButtons";
import DataGrid from "../DataGrid/DataGrid";
import LogoGrid from "../LogoGrid/LogoGrid";
import GridHeader from "../GridHeader/GridHeader";
import ImageModal from "../ImageModal/ImageModal";
import { useParams, Outlet } from "react-router-dom";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import ImageModalOther from "../ImageModalOther/ImageModalOther";

const GridPage = () => {
  const startDate = new Date();
  startDate.setHours(18, 0, 0, 0);
  const axiosPrivate = useAxiosPrivate();

  const { concertId } = useParams<{ concertId: string }>();
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentEvent, setCurrentEvent] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedBand, setSelectedBand] = useState<string>("");
  const [selectedBandId, setSelectedBandId] = useState<number | null>(null);
  const [bandImage, setBandImage] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);

  const [modalOtherOpen, setModalOtherOpen] = useState(false);
  const [currentEventName, setCurrentEventName] = useState<string>("");
  const [currentEventId, setCurrentEventId] = useState<number | null>(null);

  const handleRefresh = () => {
    setRefreshKey((prevKey) => prevKey + 1);
  };

  const handleEventClick = async (eventName: string, eventType: number, bandId: number, eventId: number) => {
    if (eventType === 1 || eventType === 2)
    {
      if (!bandId) {
        console.log("No bandId provided for:", eventName);
        return;
      }
      setSelectedBand(eventName);
      setSelectedBandId(bandId);
      setBandImage(null);
      setIsImageLoading(true);
      
      try {
        const imagePath = `/images/band/${bandId}`;
        const response = await axiosPrivate.get(imagePath, {
          responseType: 'blob',
          headers: {
            'Accept': 'image/*'
          }
        });
        
        if (response.data.size > 0) {
          const blob = new Blob([response.data], { type: response.headers['content-type'] });
          const imageUrl = URL.createObjectURL(blob);
          setBandImage(imageUrl);
        }
      } catch (error: any) {
        if (error.response && error.response.status === 404) {
          console.log("No image available for band:", bandId);
        } else {
          console.error("Error fetching band image:", error);
          console.log("Failed bandId:", bandId);
        }
        setBandImage(null);
      } finally {
        setIsImageLoading(false);
        setModalOpen(true);
      }
    }
    else{
      setCurrentEventName(eventName);
      setCurrentEventId(eventId);
      setModalOtherOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    if (bandImage) {
      URL.revokeObjectURL(bandImage);
    }
    setBandImage(null);
    setSelectedBandId(null);
    setIsImageLoading(false);
  };

  const handleCloseOtherModal = () => {
    setModalOtherOpen(false);
    setCurrentEventName("");
    setCurrentEventId(null);
  };

  const handleImageUpload = async (file: File) => {
    if (!selectedBandId) return;

    const formData = new FormData();
    formData.append('band', file);

    setIsImageLoading(true);
    try {
      await axiosPrivate.post(`/images/band/${selectedBandId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const response = await axiosPrivate.get(`/images/band/${selectedBandId}`, {
        responseType: 'blob',
        headers: {
          'Accept': 'image/*'
        }
      });
      
      if (response.data.size > 0) {
        const blob = new Blob([response.data], { type: response.headers['content-type'] });
        const imageUrl = URL.createObjectURL(blob);
        setBandImage(imageUrl);
      }
    } catch (error) {
      console.error('Error uploading image:', error);
    } finally {
      setIsImageLoading(false);
    }
  };

  return (
    <div className="grid-page">
      <GridHeader concertId={Number(concertId)} currentEvent={currentEvent} />
      {window.innerWidth >= 1246 ? (
        <LogoGrid />
      ) : null}      

      <DataGrid 
        key={refreshKey} 
        startDate={startDate} 
        concertId={Number(concertId)} 
        setCurrentEvent={setCurrentEvent}
        onEventClick={handleEventClick}
        onRefresh={handleRefresh}
      />
      <GridButtons concertId={Number(concertId)} refreshGridData={handleRefresh}/>
      <Outlet context={{ onRefresh: handleRefresh }}/>
      <ImageModal 
        isOpen={modalOpen}
        onClose={handleCloseModal}
        bandName={selectedBand}
        bandId={selectedBandId ?? 0}
        imageUrl={bandImage}
        isLoading={isImageLoading}
        onImageUpload={handleImageUpload}
      />
      <ImageModalOther 
        isOpen={modalOtherOpen}
        onClose={handleCloseOtherModal}
        eventName={currentEventName}
        eventId={currentEventId ?? 0}
        onRefresh={handleRefresh} 
      />
    </div>
  );
};

export default GridPage;
